import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { reportAccessSecondaryCategoryQueryConfig } from '@webapp/platform/iam/query-configs/reportAccessSecondaryCategory'
import { SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'

type UseQueryReportAccessSecondaryCategoriesReturn = {
  secondaryCategoryConfigs: SecondaryCategoryConfig[]
  pagination?: RepoPaginationResponse
  isLoading: boolean
  isFetching: boolean
  isPreviousData: boolean
  error: unknown
  refetch: () => void
}

export const useQueryReportAccessSecondaryCategories = (
  primaryCategoryId: number | undefined,
  params?: RepoFilterParams
): UseQueryReportAccessSecondaryCategoriesReturn => {
  const { data, isLoading, isFetching, isPreviousData, error, refetch } =
    useQuery({
      ...reportAccessSecondaryCategoryQueryConfig.list(
        primaryCategoryId,
        params
      ),
      enabled: primaryCategoryId !== undefined,
      // Keep the previous page rendered while refetching (overlay loader parity)
      keepPreviousData: true,
    })

  const secondaryCategoryConfigs = useMemo(
    () =>
      (data?.data ?? []).map((config) => new SecondaryCategoryConfig(config)),
    [data]
  )

  return {
    secondaryCategoryConfigs,
    pagination: data?.pagination,
    isLoading,
    isFetching,
    isPreviousData,
    error,
    refetch,
  }
}
